import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, XCircle } from 'lucide-react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import InlineAlert from '../../components/common/InlineAlert';
import { StatusChip } from '../../components/ui/StatusChip';
import { formatAmount } from '../../utils/numberFormat';
import type { ServiceEngagement, ServiceEngagementStatus } from '../../services/serviceEngagementService';
import { ENGAGEMENT_ICONS, nextStatus, safeHttpUrl } from './engagementMeta';

export interface EngagementCardProps {
  engagement: ServiceEngagement;
  /** On the "incoming" tab the provider moves the request along. */
  asProvider: boolean;
  /** Moves the request to `status`. Resolves to an i18n error key, or null on success. */
  onUpdate: (
    engagement: ServiceEngagement,
    status: ServiceEngagementStatus,
    deliverableUrl?: string
  ) => Promise<string | null>;
  /** The status this card is moving to right now, if any. */
  pendingStatus: ServiceEngagementStatus | null;
  /** True while any update on the page is in flight. */
  busy: boolean;
}

type Mode = 'idle' | 'deliver' | 'confirmCancel';

const LABEL = 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]';

/**
 * One service request: title, service, proposed fee, details, status and, once
 * delivered, the link to the work. On the provider's side it carries the next
 * step, an inline form for the delivery link (no browser prompt), and a cancel
 * action behind an inline confirmation.
 */
export default function EngagementCard({
  engagement: e,
  asProvider,
  onUpdate,
  pendingStatus,
  busy,
}: EngagementCardProps) {
  // 'common' for the shared suspended-account message an update can come back with.
  const { t } = useTranslation(['engage', 'common']);
  const uid = useId();
  const ids = {
    title: `${uid}-title`,
    url: `${uid}-url`,
    urlHint: `${uid}-url-hint`,
    confirm: `${uid}-confirm`,
  };

  const [mode, setMode] = useState<Mode>('idle');
  const [deliverUrl, setDeliverUrl] = useState('');
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLParagraphElement>(null);
  const cancelTriggerRef = useRef<HTMLButtonElement>(null);
  const advanceRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useRef<'url' | 'confirm' | 'cancelTrigger' | 'advance' | null>(null);

  useEffect(() => {
    const target = pendingFocus.current;
    pendingFocus.current = null;
    if (target === 'url') urlRef.current?.focus();
    else if (target === 'confirm') confirmRef.current?.focus();
    else if (target === 'cancelTrigger') cancelTriggerRef.current?.focus();
    else if (target === 'advance') advanceRef.current?.focus();
  }, [mode]);

  const next = asProvider ? nextStatus(e.status) : null;
  const deliverable = safeHttpUrl(e.deliverableUrl);
  const hasFee = typeof e.feeAmount === 'number' && e.feeAmount > 0;

  const goIdle = (focus: 'cancelTrigger' | 'advance') => {
    pendingFocus.current = focus;
    setDeliverError(null);
    setMode('idle');
  };

  const run = async (status: ServiceEngagementStatus, url?: string) => {
    setError(null);
    const failure = await onUpdate(e, status, url);
    if (failure) {
      setError(failure);
      return;
    }
    setMode('idle');
    setDeliverUrl('');
    // The button that was pressed may be gone (a finished or cancelled request has
    // no next step); keep focus on this card.
    titleRef.current?.focus();
  };

  const submitDeliver = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const url = deliverUrl.trim();
    if (url && !safeHttpUrl(url)) {
      setDeliverError('card.deliverInvalid');
      urlRef.current?.focus();
      return;
    }
    void run('DELIVERED', url || undefined);
  };

  return (
    <li>
      <article
        aria-labelledby={ids.title}
        className="rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] p-4 shadow-brand-xs sm:p-5"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3
              id={ids.title}
              ref={titleRef}
              tabIndex={-1}
              className="text-base font-bold leading-snug text-[var(--pv-color-text)] [overflow-wrap:anywhere] focus:outline-none"
            >
              {e.title}
            </h3>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.875rem] text-[var(--pv-color-text-muted)]">
              <span className="inline-flex items-center gap-1.5">
                <i
                  className={`${ENGAGEMENT_ICONS[e.type] ?? 'ri-briefcase-line'} text-[1rem] leading-none text-[var(--pv-color-primary)]`}
                  aria-hidden="true"
                />
                {t(`types.${e.type}`)}
              </span>
              {hasFee ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="inline-flex items-center gap-1">
                    {t('card.fee')}
                    <span className="inline-flex items-center gap-1 font-bold text-[var(--pv-color-text)]">
                      <SaudiRiyalSymbol title={t('card.riyal')} className="h-[0.875rem] w-[0.875rem] shrink-0" />
                      {formatAmount(e.feeAmount, { maximumFractionDigits: 0 })}
                    </span>
                  </span>
                </>
              ) : null}
            </p>
            {e.description ? (
              <p className="mt-2 line-clamp-3 whitespace-pre-line text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)] [overflow-wrap:anywhere]">
                {e.description}
              </p>
            ) : null}
            {deliverable ? (
              <a
                href={deliverable}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg text-[0.9375rem] font-bold text-[#1B7566] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:text-[#7FD6C3]"
              >
                <ExternalLink className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
                {t('card.deliverable')}
                <span className="sr-only"> {t('card.opensInNewTab')}</span>
              </a>
            ) : null}
          </div>
          <StatusChip status={e.status} label={t(`statuses.${e.status}`)} className="shrink-0 self-start" />
        </div>

        {next ? (
          <div className="mt-4 border-t border-[var(--pv-border-subtle)] pt-4">
            {mode === 'deliver' ? (
              <form noValidate onSubmit={submitDeliver} className="space-y-3">
                <div>
                  <label htmlFor={ids.url} className={LABEL}>
                    {t('card.deliverLabel')}
                  </label>
                  <Input
                    ref={urlRef}
                    id={ids.url}
                    type="url"
                    dir="ltr"
                    inputMode="url"
                    value={deliverUrl}
                    onChange={(event) => {
                      setDeliverUrl(event.target.value);
                      if (deliverError) setDeliverError(null);
                    }}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="https://"
                    invalid={deliverError !== null}
                    aria-invalid={deliverError !== null || undefined}
                    aria-describedby={ids.urlHint}
                  />
                  <p
                    id={ids.urlHint}
                    className={`mt-1.5 text-[0.875rem] leading-relaxed ${
                      deliverError ? 'text-[#C62828] dark:text-[var(--pv-color-red)]' : 'text-[var(--pv-color-text-muted)]'
                    }`}
                  >
                    {t(deliverError ?? 'card.deliverHint')}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button type="submit" size="sm" loading={pendingStatus === 'DELIVERED'} disabled={busy}>
                    {t('card.deliverConfirm')}
                  </Button>
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => goIdle('advance')}>
                    {t('card.keep')}
                  </Button>
                </div>
              </form>
            ) : mode === 'confirmCancel' ? (
              <div
                role="group"
                aria-labelledby={ids.confirm}
                className="rounded-xl border border-[#DF2F2B]/25 bg-[var(--pv-color-red-soft)] p-4 dark:border-[#FF8A80]/30"
              >
                <p
                  id={ids.confirm}
                  ref={confirmRef}
                  tabIndex={-1}
                  className="text-[0.9375rem] leading-relaxed text-[var(--pv-color-text)] focus:outline-none"
                >
                  {t('card.cancelConfirm')}
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button
                    variant="danger"
                    size="sm"
                    loading={pendingStatus === 'CANCELLED'}
                    disabled={busy}
                    onClick={() => void run('CANCELLED')}
                  >
                    {t('card.cancelYes')}
                  </Button>
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => goIdle('cancelTrigger')}>
                    {t('card.keep')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  ref={advanceRef}
                  size="sm"
                  loading={pendingStatus === next}
                  disabled={busy}
                  onClick={() => {
                    if (next === 'DELIVERED') {
                      pendingFocus.current = 'url';
                      setError(null);
                      setMode('deliver');
                    } else {
                      void run(next);
                    }
                  }}
                >
                  {t(`advance.${next}`)}
                </Button>
                <button
                  ref={cancelTriggerRef}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    pendingFocus.current = 'confirm';
                    setError(null);
                    setMode('confirmCancel');
                  }}
                  className="inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl px-3 text-[0.875rem] font-bold text-[#C62828] transition-colors duration-200 hover:bg-[var(--pv-color-red-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C62828]/30 disabled:cursor-not-allowed disabled:opacity-50 dark:text-[var(--pv-color-red)]"
                >
                  <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {t('card.cancelRequest')}
                </button>
              </div>
            )}
            {error ? (
              <InlineAlert live="alert" className="mt-3">
                {t(error)}
              </InlineAlert>
            ) : null}
          </div>
        ) : null}
      </article>
    </li>
  );
}
